import React, { FC, memo, use, useState } from "react";
import { CopyOutlined, EditOutlined, ExportOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Flex, Tooltip } from "antd";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { DmsPaths } from "@dms/configs/constants";
import { tableModeEnum } from "@dms/types";
import { DropdownActions } from "./DropdownActions";
import { ICellRendererParams } from "ag-grid-community";
import { DmsAppControlContext, DmsDataContext } from "@dms/types/ContextTypes";
import { GQL } from "@binale-tech/shared";
import { DmsModalPdfViewer } from "@dms/components/ModalPdfViewer/DmsModalPdfViewer";
import { PlaceholderActions } from "@app/components/shared/Table2/PlaceholderActions";
import { useRenderOnScreen } from "../../../../../scripts/infrastructure/hooks/useOnScreen";

export type TableActionsCellProps = {
    mode: tableModeEnum;
};

export const CellRendererTableActionList: FC<ICellRendererParams<GQL.IDocument> & TableActionsCellProps> = memo(
    function TableActionList(props) {
        const ref = React.useRef<HTMLDivElement>(null);
        const shouldRender = useRenderOnScreen(ref);

        return <div ref={ref}>{shouldRender ? <TableActionListContent {...props} /> : <PlaceholderActions />}</div>;
    }
);

const TableActionListContent: FC<ICellRendererParams<GQL.IDocument> & TableActionsCellProps> = ({
    mode,
    node,
    data,
}) => {
    const [title, setTitle] = useState(<FormattedMessage id="app.dms.copyId" />);

    const { selectRow, typeChangeModeToggle } = use(DmsAppControlContext);
    const { documentsKV } = use(DmsDataContext);
    const [viewerDocument, setViewerDocument] = useState<GQL.IDocument>();

    const document = documentsKV[data.key];

    if (!document) {
        return null;
    }
    const modal = <DmsModalPdfViewer document={viewerDocument} onClose={() => setViewerDocument(undefined)} />;

    if (mode === tableModeEnum.modal) {
        return (
            <Flex align="center" justify="center">
                <Tooltip destroyOnHidden title={<FormattedMessage id="app.dms.view" />}>
                    <a
                        onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            setViewerDocument(document);
                        }}
                    >
                        <EyeOutlined style={{ fontSize: "16px" }} />
                    </a>
                </Tooltip>
                {modal}
            </Flex>
        );
    }

    const copyIdNumber = async (e: React.MouseEvent<HTMLElement>, id: string) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(id);
        setTitle(<FormattedMessage id="app.dms.idCopied" />);
    };

    const resetTooltip = (open: boolean) => {
        if (!open) {
            setTitle(<FormattedMessage id="app.dms.copyId" />);
        }
    };

    return (
        <Flex
            align="center"
            justify="center"
            gap={4}
            onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        >
            <Tooltip
                destroyOnHidden
                title={title}
                onOpenChange={(open: boolean) => {
                    resetTooltip(open);
                }}
            >
                <Button
                    icon={<CopyOutlined />}
                    type="text"
                    shape="circle"
                    style={{ padding: 0 }}
                    onClick={async e => {
                        await copyIdNumber(e, document?.key);
                    }}
                />
            </Tooltip>
            <Tooltip destroyOnHidden title={<FormattedMessage id="app.dms.view" />}>
                <Button
                    data-testid={"dms-table-row-actions-preview"}
                    icon={<EyeOutlined style={{ fontSize: "16px" }} />}
                    type="text"
                    shape="circle"
                    style={{ padding: 0 }}
                    onClick={() => setViewerDocument(document)}
                />
            </Tooltip>
            <Tooltip destroyOnHidden title={<FormattedMessage id="app.button.edit" />}>
                <Link
                    to={`/${DmsPaths.ROOT}/${DmsPaths.EDITING}/${document.key}`}
                    state={{ isGroup: false, documentId: document.key }}
                >
                    <Button
                        data-testid={"dms-table-row-actions-edit"}
                        shape="circle"
                        type="text"
                        icon={<EditOutlined style={{ fontSize: "16px" }} />}
                        style={{ padding: 0 }}
                    />
                </Link>
            </Tooltip>
            <Tooltip destroyOnHidden title={<FormattedMessage id="app.dms.moveTo" />}>
                <Button
                    icon={<ExportOutlined rotate={180} />}
                    shape="circle"
                    type="text"
                    onClick={() => {
                        selectRow(document.key);
                        typeChangeModeToggle(true);
                    }}
                />
            </Tooltip>
            <Tooltip destroyOnHidden title={<FormattedMessage id="app.dms.more" />}>
                <DropdownActions document={document} mode="table" node={node} />
            </Tooltip>
            {modal}
        </Flex>
    );
};
